import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack } from "@mui/material";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { useMutation, useQuery } from "react-query";
import { ApplicationsService, ApplicationSettingsPreset, MtBillingType } from "gen/clients";
import * as Yup from "yup";

interface Props {
  applicationId: string;
  existingPresetNames: string[];
  existingPresetIds: string[];
  onClose: () => void;
  onRefresh: () => void;
  onPresetCreated: (presetId: string) => void;
}

enum FieldName {
  presetId = "presetId",
  presetName = "presetName"
}

interface FormValues {
  [FieldName.presetId]: string;
  [FieldName.presetName]: string;
}

// Generate a unique preset ID
const generateUniquePresetId = (existingIds: string[]): string => {
  const timestamp = Date.now();
  let counter = 0;
  let candidateId = `preset_${timestamp}`;

  while (existingIds.includes(candidateId)) {
    counter++;
    candidateId = `preset_${timestamp}_${counter}`;
  }

  return candidateId;
};

const CreatePresetDialog: React.FC<Props> = ({
  applicationId,
  existingPresetNames,
  existingPresetIds,
  onClose,
  onRefresh,
  onPresetCreated
}) => {
  const {
    data: application,
    isLoading: isApplicationLoading,
    error: applicationError
  } = useQuery(["getApplication", applicationId], {
    queryFn: () => ApplicationsService.getApplication({ id: applicationId }),
    refetchOnMount: true
  });

  const {
    mutateAsync,
    isLoading: isMutating,
    error: mutationError
  } = useMutation(ApplicationsService.updateApplication);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        [FieldName.presetId]: Yup.string()
          .required("Preset ID is required")
          .test(
            "unique",
            "A preset with this ID already exists",
            value => !value || !existingPresetIds.includes(value.trim())
          ),
        [FieldName.presetName]: Yup.string()
          .required("Preset name is required")
          .test(
            "unique",
            "A preset with this name already exists",
            value => !value || !existingPresetNames.includes(value.trim())
          )
      }),
    [existingPresetNames, existingPresetIds]
  );

  const initialValues = React.useMemo<FormValues>(
    () => ({
      [FieldName.presetId]: generateUniquePresetId(existingPresetIds),
      [FieldName.presetName]: ""
    }),
    [existingPresetIds]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (!application) {
        return;
      }

      const presetId = values[FieldName.presetId].trim();
      const presetName = values[FieldName.presetName].trim();
      if (!presetId || !presetName) {
        return;
      }

      // Create new preset with current default settings
      const newPreset: ApplicationSettingsPreset = {
        id: presetId,
        name: presetName,
        settings: application.settings || {
          billingType: MtBillingType.FREE,
          maxRequestsPerUserPerDay: 0,
          maxCharactersPerRequest: 0,
          supportedLltsLanguages: [],
          maxFilesPerRequest: 0,
          maxFileSizeMB: 0,
          maxAudioFileSizeMB: 0,
          maxVideoFileSizeMB: 0
        }
      };

      // Update application with new preset
      const updatedApplication = {
        ...application,
        settingPresets: [...(application.settingPresets || []), newPreset]
      };

      await mutateAsync({
        id: application.id,
        requestBody: updatedApplication
      });

      // Call callbacks after successful API call
      onPresetCreated(newPreset.id);
      onRefresh();
      onClose();
    },
    [application, mutateAsync, onPresetCreated, onRefresh, onClose]
  );

  // Show loading state while fetching application data
  if (isApplicationLoading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
        <DialogTitle>Create New Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ minHeight: 100, justifyContent: "center", alignItems: "center" }}>
            Loading...
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error if application failed to load
  if (applicationError) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
        <DialogTitle>Create New Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <ApiErrorMessage apiError={applicationError} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
      <DialogTitle>Create New Preset</DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnMount={false}
        validateOnChange={true}
      >
        {() => (
          <Form noValidate={true} autoComplete="off">
            <DialogContent>
              <Stack spacing={3}>
                <TextInputField
                  name={FieldName.presetId}
                  label="Preset ID"
                  required={true}
                  helperText="Enter a unique ID for this preset"
                />
                <TextInputField
                  name={FieldName.presetName}
                  label="Preset Name"
                  required={true}
                  helperText="Enter a unique name for this preset"
                />
                {mutationError && <ApiErrorMessage apiError={mutationError} />}
              </Stack>
            </DialogContent>
            <DialogActions>
              <LoadingButton type="submit" color="primary" variant="contained" isLoading={isMutating}>
                Create
              </LoadingButton>
              <Button color="secondary" onClick={onClose} disabled={isMutating}>
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { CreatePresetDialog };
